import { useMutation } from "@tanstack/vue-query";
import api from "@/services/api";

/** Maximum file size for image uploads (10MB) */
export const MAX_IMAGE_UPLOAD_SIZE = 10_000_000;

const MAX_IMAGE_UPLOAD_SIZE_MB = MAX_IMAGE_UPLOAD_SIZE / 1_000_000;

export const IMAGE_UPLOAD_FORMATS_HINT = `PNG, JPG up to ${MAX_IMAGE_UPLOAD_SIZE_MB}MB`;
export const SINGLE_FILE_UPLOAD_ERROR =
  "Please upload only one file at a time.";

export function formatFileSize(bytes: number): string {
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)}MB`;
  return `${(bytes / 1_000).toFixed(0)}KB`;
}

export function fileTooLargeMessage(bytes: number): string {
  return `File is too large (${formatFileSize(bytes)}). Maximum size is ${MAX_IMAGE_UPLOAD_SIZE_MB}MB.`;
}

export interface ImageUploadResponse {
  url: string;
  key: string;
}

/**
 * Upload an image file to CDN.
 *
 * Accepts PNG or JPG files up to 10MB.
 * Returns the full CDN URL that can be used in coverImage fields
 * or TipTap editor content.
 *
 * @param file - The image file to upload
 * @param signal - Optional AbortSignal to cancel the upload
 */
export function uploadImage(
  file: File,
  signal?: AbortSignal,
): Promise<ImageUploadResponse> {
  // FormData is required for file uploads - it encodes the file as multipart/form-data,
  // which is the standard way to send binary data (like images) over HTTP.
  // The "image" key must match the field name expected by the backend serializer.
  const formData = new FormData();
  formData.append("image", file);

  return api
    .post<ImageUploadResponse>("/utils/upload_image/", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      signal,
    })
    .then((response) => response.data);
}

export function useUploadImageMutation() {
  return useMutation({
    mutationFn: (file: File) => uploadImage(file),
  });
}
